/**
 * Shared component for displaying leave period (date range) with optional AM/PM badges.
 * Used across LeaveManagement, LeaveCalendar, LeaveBalanceManager, LeaveApplicationDetailModal.
 *
 * AM/PM chips use SessionPeriodChip (DS sky/slate) — see design-system-reference.md
 * § AM / PM Session Chips.
 *
 * Multi-day same-year ranges omit the year on the start date (e.g. `22 Jul → 23 Jul 2026`)
 * to match formatDateRange same-year dedup and keep table Period columns single-line.
 */
import React from 'react';
import { Calendar } from 'lucide-react';
import { formatDate, formatDateShort, getYearInSingapore } from '../../utils/dateUtils';
import { getLeavePeriodDisplayChips } from '../../utils/leaveDisplayUtils';
import { SessionPeriodChip } from '../UI/SessionPeriodChip';

/**
 * @param {Object} props
 * @param {Object} [props.application] - Leave application object (preferred)
 * @param {string} [props.startDate] - Override start date (when not using application)
 * @param {string} [props.endDate] - Override end date (when not using application)
 * @param {string} [props.startTimePeriod] - 'AM' | 'PM' for half-day start
 * @param {string} [props.endTimePeriod] - 'AM' | 'PM' for half-day end
 * @param {number|string} [props.totalDays] - Total days (0.5 = half day)
 * @param {number|string} [props.leaveDays] - Alternative field for days
 * @param {boolean} [props.showIcon=true] - Show calendar icon
 * @param {boolean} [props.noWrap=false] - Keep dates on a single line (no wrapping)
 * @param {string} [props.size='sm'] - 'sm' (text-sm, icon w-4) | 'xs' (text-xs, icon w-3.5)
 * @param {string} [props.className] - Additional class names
 */
const LeavePeriodDisplay = ({
  application,
  startDate: startDateProp,
  endDate: endDateProp,
  startTimePeriod: startTimePeriodProp,
  endTimePeriod: endTimePeriodProp,
  totalDays: totalDaysProp,
  leaveDays: leaveDaysProp,
  showIcon = true,
  noWrap = false,
  size = 'sm',
  className = ''
}) => {
  const startDate = startDateProp ?? application?.leave_start_date ?? application?.start_date;
  const endDate = endDateProp ?? application?.leave_end_date ?? application?.end_date;
  const startTimePeriod = startTimePeriodProp ?? application?.leave_start_time_period;
  const endTimePeriod = endTimePeriodProp ?? application?.leave_end_time_period;
  const totalDays = totalDaysProp ?? application?.leave_total_days ?? application?.leave_days;
  const leaveDays = leaveDaysProp ?? application?.leave_days;
  const resolvedChips = application
    ? getLeavePeriodDisplayChips(application)
    : {};

  const isHalfDay = parseFloat(totalDays) === 0.5 || parseFloat(leaveDays) === 0.5;
  const durationIsFractional = Number.isFinite(parseFloat(totalDays))
    && parseFloat(totalDays) % 1 !== 0;
  // Same-day = calendar dates only. Never use leave_total_days: for OSL/HL that
  // field is deductible working days, which can be 1 while the MC still spans
  // multiple calendar days (rest days / public holidays excluded).
  const isSameDay = !startDate || !endDate || startDate === endDate;

  const iconSize = size === 'xs' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const textSize = size === 'xs' ? 'text-xs' : 'text-sm';

  const startChipSession = resolvedChips.start || (isSameDay
    ? (isHalfDay ? startTimePeriod : null)
    : (durationIsFractional && startTimePeriod === 'PM' ? 'PM' : null));
  const endChipSession = !isSameDay
    ? (resolvedChips.end || (durationIsFractional && endTimePeriod === 'AM' ? 'AM' : null))
    : null;

  const startYear = getYearInSingapore(startDate);
  const endYear = getYearInSingapore(endDate);
  const startLabel = !isSameDay && startYear != null && endYear != null && startYear === endYear
    ? formatDateShort(startDate)
    : formatDate(startDate);
  const endLabel = formatDate(endDate);
  const titleText = isSameDay
    ? `${formatDate(startDate)}${startChipSession ? ` ${startChipSession}` : ''}`
    : `${formatDate(startDate)}${startChipSession ? ` ${startChipSession}` : ''} → ${endLabel}${endChipSession ? ` ${endChipSession}` : ''}`;

  const dateContent = isSameDay ? (
    <>
      <span className="inline-flex items-center gap-1">
        <span className="tabular-nums">{formatDate(startDate)}</span>
        <SessionPeriodChip session={startChipSession} />
      </span>
    </>
  ) : (
    <>
      <span className="inline-flex items-center gap-1">
        <span className="font-medium tabular-nums">{startLabel}</span>
        <SessionPeriodChip session={startChipSession} />
      </span>
      <span className="text-gray-400 mx-0.5 flex-shrink-0" aria-hidden="true">→</span>
      <span className="inline-flex items-center gap-1">
        <span className="font-medium tabular-nums">{endLabel}</span>
        <SessionPeriodChip session={endChipSession} />
      </span>
    </>
  );

  return (
    <div
      className={`flex items-center gap-2 ${textSize} text-gray-900 ${className}`}
      title={titleText}
    >
      {showIcon && (
        <Calendar className={`${iconSize} text-gray-400 flex-shrink-0`} />
      )}
      <div
        className={`flex items-center ${noWrap ? 'flex-nowrap whitespace-nowrap gap-1' : 'flex-wrap gap-1.5'}`}
      >
        {dateContent}
      </div>
    </div>
  );
};

export default LeavePeriodDisplay;
